import { useState, useEffect } from 'react'
import { FlaskConical, X, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../../services/api'

// On garde une base de secours si l'API est vide.
const DEFAULT_GROUPS =[
    { title: "Bilan Glucidique", items: ["Glycémie à jeun", "HbA1c"] },
    { title: "Bilan Lipidique", items: ["Cholestérol Total", "HDL", "LDL", "Triglycérides"] }
]

export default function PrescriptionGeneratorModal({ patient, onClose, onSuccess }) {
    const[selectedAnalyses, setSelectedAnalyses] = useState(new Set())
    const [customNotes, setCustomNotes] = useState('')
    const [loading, setLoading] = useState(false)
    const [groups, setGroups] = useState([])

    useEffect(() => {
        api.get('/api/settings/')
            .then(res => {
                const setting = res.data.find(s => s.setting_key === 'biological_analyses_config')
                if (setting && setting.setting_value) {
                    setGroups(JSON.parse(setting.setting_value))
                } else {
                    setGroups(DEFAULT_GROUPS)
                }
            }).catch(() => setGroups(DEFAULT_GROUPS))
    },[])

    const toggleAnalysis = (item) => {
        const newSet = new Set(selectedAnalyses)
        if (newSet.has(item)) newSet.delete(item)
        else newSet.add(item)
        setSelectedAnalyses(newSet)
    }

    const toggleGroup = (groupItems) => {
        const newSet = new Set(selectedAnalyses)
        const allSelected = groupItems.every(i => newSet.has(i))
        groupItems.forEach(i => {
            if (allSelected) newSet.delete(i)
            else newSet.add(i)
        })
        setSelectedAnalyses(newSet)
    }

    const handleGenerate = async () => {
        if (selectedAnalyses.size === 0) return toast.error("Cochez au moins une analyse.")
        
        // Magie : on regroupe les éléments sélectionnés selon la structure
        const groupedAnalyses = groups.map(g => ({
            group: g.title,
            items: g.items.filter(i => selectedAnalyses.has(i))
        })).filter(g => g.items.length > 0);

        setLoading(true)
        try {
            await api.post(`/api/documents/patient/${patient.id}/generate-prescription`, {
                analyses: groupedAnalyses,
                notes: customNotes
            })

            toast.success("Prescription générée et enregistrée au dossier !")
            onSuccess()
            onClose()
        } catch (error) { toast.error("Erreur lors de la génération") } 
        finally { setLoading(false) }
    }

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col h-[90vh]">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-blue-50 rounded-t-2xl">
                    <h3 className="font-serif font-bold text-xl text-blue-900 flex items-center gap-2">
                        <FlaskConical className="w-5 h-5 text-blue-600"/> Générateur de Prescription d'Analyses
                    </h3>
                    <button onClick={onClose}><X className="w-6 h-6 text-gray-400 hover:text-gray-600" /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {groups.map((group, idx) => {
                            const groupSelected = group.items.length > 0 && group.items.every(i => selectedAnalyses.has(i))
                            return (
                                <div key={idx} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                                    <div className="flex justify-between items-center mb-3 border-b border-gray-100 pb-2">
                                        <h4 className="font-bold text-gray-800 text-sm">{group.title}</h4>
                                        <button 
                                            onClick={() => toggleGroup(group.items)}
                                            className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${groupSelected ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                        >
                                            Tout cocher
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        {group.items.map(item => (
                                            <label key={item} className="flex items-start gap-2 cursor-pointer group">
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedAnalyses.has(item)}
                                                    onChange={() => toggleAnalysis(item)}
                                                    className="mt-0.5 w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                                                />
                                                <span className="text-sm text-gray-700 group-hover:text-black leading-tight">{item}</span>
                                            </label>
                                        ))}
                                        {group.items.length === 0 && <p className="text-xs text-gray-400 italic">Vide</p>}
                                    </div>
                                </div>
                            )
                        })}
                    </div>

                    <div className="mt-6 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Remarques complémentaires (Optionnel)</label>
                        <textarea 
                            rows={2} 
                            value={customNotes}
                            onChange={e => setCustomNotes(e.target.value)}
                            placeholder="Ex: Merci de rechercher également une intolérance au gluten..."
                            className="w-full text-sm bg-gray-50 focus:bg-white border-gray-200 rounded-lg"
                        />
                    </div>
                </div>

                <div className="p-4 border-t border-gray-100 bg-white rounded-b-2xl flex justify-between items-center shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
                    <span className="text-sm font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                        {selectedAnalyses.size} analyse(s) sélectionnée(s)
                    </span>
                    <div className="flex gap-3">
                        <button onClick={onClose} className="btn bg-gray-100 text-gray-600 hover:bg-gray-200 px-6">Annuler</button>
                        <button onClick={handleGenerate} disabled={loading || selectedAnalyses.size === 0} className="btn btn-primary px-8 flex items-center gap-2">
                            {loading ? <div className="spinner w-4 h-4 text-white"/> : <><Save className="w-4 h-4"/> Générer l'ordonnance</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}